import { useSuspenseQuery } from '@tanstack/react-query';
import {
  FakeUserCreateInput,
  INTEREST_CATALOG,
  type FakeUserCreateInput as Input,
  type InterestSlug,
  MAX_INTERESTS,
} from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input as UiInput } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { Switch } from '@upme/ui/components/switch';
import { useForm } from 'react-hook-form';
import { zodResolver } from '../../../app/zod-resolver';
import { listCitiesOptions } from '../../cities/queries';

interface Props {
  initialValues?: Partial<Input>;
  submitLabel: string;
  onSubmit: (values: Input) => Promise<unknown> | unknown;
}

export function FakeForm({ initialValues, submitLabel, onSubmit }: Props) {
  const { data: cities } = useSuspenseQuery(listCitiesOptions());

  const form = useForm<Input>({
    resolver: zodResolver(FakeUserCreateInput),
    defaultValues: {
      firstName: '',
      lastName: undefined,
      cityId: cities[0]?.id ?? '',
      bio: undefined,
      interests: [],
      isDiscoverable: true,
      ...initialValues,
    },
  });

  const interests = form.watch('interests') ?? [];
  const atLimit = interests.length >= MAX_INTERESTS;

  const toggleInterest = (slug: InterestSlug) => {
    const next = interests.includes(slug)
      ? interests.filter((x) => x !== slug)
      : interests.length >= MAX_INTERESTS
        ? interests
        : [...interests, slug];
    form.setValue('interests', next, { shouldValidate: true });
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  const err = (k: keyof Input) => {
    const e = form.formState.errors[k] as { message?: string } | undefined;
    return e?.message ? <p className="text-sm text-destructive mt-1">{String(e.message)}</p> : null;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-xl" noValidate>
      <div>
        <Label htmlFor="firstName">Имя</Label>
        <UiInput id="firstName" {...form.register('firstName')} />
        {err('firstName')}
      </div>

      <div>
        <Label htmlFor="lastName">Фамилия (опционально)</Label>
        <UiInput id="lastName" {...form.register('lastName')} />
        {err('lastName')}
      </div>

      <div>
        <Label>Город</Label>
        <Select
          value={form.watch('cityId')}
          onValueChange={(v) => form.setValue('cityId', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Выберите город" />
          </SelectTrigger>
          <SelectContent>
            {cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {err('cityId')}
      </div>

      <div>
        <Label htmlFor="bio">Био (до 280 символов)</Label>
        <textarea
          id="bio"
          rows={3}
          maxLength={280}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('bio')}
        />
        {err('bio')}
      </div>

      <div>
        <Label>
          Интересы · {interests.length}/{MAX_INTERESTS}
        </Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {INTEREST_CATALOG.map(({ slug, label }) => {
            const active = interests.includes(slug);
            return (
              <button
                key={slug}
                type="button"
                aria-pressed={active}
                disabled={!active && atLimit}
                onClick={() => toggleInterest(slug)}
                className={
                  active
                    ? 'rounded-full border border-primary bg-primary px-3 py-1 text-xs font-medium text-primary-foreground'
                    : 'rounded-full border border-input bg-transparent px-3 py-1 text-xs text-muted-foreground disabled:opacity-40'
                }
              >
                {label}
              </button>
            );
          })}
        </div>
        {err('interests')}
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="isDiscoverable">Discoverable</Label>
        <Switch
          id="isDiscoverable"
          checked={form.watch('isDiscoverable') ?? true}
          onCheckedChange={(v) => form.setValue('isDiscoverable', v, { shouldValidate: true })}
        />
      </div>

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {submitLabel}
      </Button>
    </form>
  );
}
